<script setup lang="ts">
import { computed } from 'vue'
import { useLanguageStore } from '../../stores/language'

const languageStore = useLanguageStore()
const footerTexts = computed(() => languageStore.footer)
const currentYear = new Date().getFullYear()
const bottomText = computed(() => footerTexts.value.bottomText.replace('{year}', `${currentYear}`))
</script>

<template>
  <footer class="footer">
    <div class="container">
      <div class="footer-content">
        <div class="footer-section">
          <h3>{{ footerTexts.companyName }}</h3>
          <p>{{ footerTexts.description }}</p>
          <div class="social-links">
            <a
              href="https://www.linkedin.com/company/efes-bilgi-teknolojileri"
              target="_blank"
              class="social-link"
              aria-label="LinkedIn"
              rel="noopener noreferrer"
            >
              <i class="pi pi-linkedin"></i>
            </a>
            <a
              href="https://www.instagram.com/efestech"
              target="_blank"
              class="social-link"
              aria-label="Instagram"
              rel="noopener noreferrer"
            >
              <i class="pi pi-instagram"></i>
            </a>
            <a
              href="https://youtube.com/@efestech"
              target="_blank"
              class="social-link"
              aria-label="YouTube"
              rel="noopener noreferrer"
            >
              <i class="pi pi-youtube"></i>
            </a>
            <a
              href="https://www.tiktok.com/@efestechcom"
              target="_blank"
              class="social-link"
              aria-label="TikTok"
              rel="noopener noreferrer"
            >
              <i class="pi pi-tiktok"></i>
            </a>
          </div>
        </div>
        <div class="footer-section">
          <h3>{{ footerTexts.quickLinksTitle }}</h3>
          <ul>
            <li v-for="link in footerTexts.quickLinks" :key="link.to">
              <router-link :to="languageStore.localizePath(link.to)">{{ link.label }}</router-link>
            </li>
          </ul>
        </div>
        <div class="footer-section">
          <h3>{{ footerTexts.contactTitle }}</h3>
          <ul class="contact-info">
            <li>
              <a :href="`mailto:${footerTexts.contactItems.email}`">
                {{ footerTexts.contactItems.email }}
              </a>
            </li>
            <li>
              <a :href="`tel:${footerTexts.contactItems.phone.replace(/\s/g, '')}`">
                {{ footerTexts.contactItems.phone }}
              </a>
            </li>
            <li>{{ footerTexts.contactItems.address }}</li>
          </ul>
        </div>
      </div>
      <div class="footer-bottom">
        <p>{{ bottomText }}</p>
      </div>
    </div>
  </footer>
</template>

<style scoped>
footer {
  background-color: var(--primary-color);
  color: var(--background-white);
  padding: 40px 20px 20px;
  width: 100%;
  position: relative;
}

.container {
  position: relative;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  padding-bottom: 2rem;
}

.footer-section h3 {
  color: #ffffff;
  font-weight: 700;
  margin-bottom: 1.5rem;
  font-size: 1.25rem;
  letter-spacing: -0.01em;
}

.footer-section p {
  color: #e2e8f0;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.social-links {
  display: flex;
  gap: 1.5rem;
  margin-top: 1rem;
}

.social-link {
  color: var(--background-white);
  font-size: 1.5rem;
  transition: all 0.3s ease;
  text-decoration: none;
}

.social-link:hover {
  color: var(--accent-color);
  transform: translateY(-3px);
}

.footer-section ul {
  list-style: none;
  padding: 0;
}

.footer-section ul li {
  margin-bottom: 0.8rem;
}

.footer-section ul li a {
  color: #f1f5f9;
  text-decoration: none;
  transition: color 0.3s ease;
}

.footer-section ul li a:hover {
  color: #93c5fd;
}

.contact-info li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #f1f5f9;
}

.contact-info li a {
  color: #f1f5f9;
  text-decoration: none;
  transition: color 0.3s ease;
}

.contact-info li a:hover {
  color: #93c5fd;
}

.contact-info i {
  color: #60a5fa;
}

.footer-bottom {
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.9rem;
  color: #cbd5e1;
}

@media (max-width: 768px) {
  .footer-content {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .social-links {
    justify-content: center;
  }

  .contact-info li {
    justify-content: center;
  }
}
</style>
